import { Alert, App as AntApp, Button, Collapse, InputNumber, List, Modal, Space, Table, Tag, Tooltip, Typography } from "antd";
import { LockOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { formatDate } from "../app/classes.ts";
import { examKeys, type ExamClassDetails, type ExamDetails, type ExamStudentMark } from "../app/exams.ts";
import { studentFullName } from "../app/grading.ts";
import { formatSchoolDateTime } from "../app/time.ts";

interface Props {
  exam: ExamDetails;
  /** The class to show; undefined keeps the dialog closed. */
  examClass: ExamClassDetails | undefined;
  onClose(): void;
}

/** A changed cell per student: the new mark, or null to remove it. */
type Draft = Record<string, number | null>;

/**
 * One class's exam sheet (GET /grading/exams/{id}/classes/{classId}): the roster on the exam date with each mark on the exam's scale.
 * The assigned grader, or staff holding grading.manage, types marks: Enter or the arrow keys move down the column, an emptied cell
 * removes the mark, and only changed cells are sent. Marks wait for the exam day and are read-only while a closed marking period
 * covers it. The server decides; the dialog only hides what the user cannot do.
 */
export function ExamMarksModal({ exam, examClass, onClose }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>({});
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});
  const [summary, setSummary] = useState<string[]>([]);
  const grid = useRef<HTMLDivElement>(null);
  const classId = examClass?.classId ?? "";
  const params = { params: { path: { examId: exam.id, classId } } };

  const sheet = useQuery({
    queryKey: examKeys.sheet(exam.id, classId),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/exams/{examId}/classes/{classId}", params);
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: examClass !== undefined,
  });
  const edits = useQuery({
    queryKey: examKeys.edits(exam.id, classId),
    queryFn: async () => {
      const { data, error } = await api.GET("/grading/exams/{examId}/classes/{classId}/edits", params);
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    enabled: examClass !== undefined,
  });

  useEffect(() => {
    setDraft({});
    setCellErrors({});
    setSummary([]);
  }, [classId]);

  const data = sheet.data;
  const editable = data !== undefined && data.mayEdit && !data.isClosed && !data.isUpcoming;
  const sent = Object.entries(draft);
  const save = useMutation({
    mutationFn: async () => {
      const marks = sent.map(([studentId, value]) => ({ studentId, value }));
      const { data: saved, error, response } = await api.PUT("/grading/exams/{examId}/classes/{classId}/marks", { ...params, body: { marks } });
      if (!saved) throw new ApiError(response.status, error);
      return saved;
    },
    onSuccess: async (saved) => {
      queryClient.setQueryData(examKeys.sheet(exam.id, classId), saved);
      setDraft({});
      setCellErrors({});
      setSummary([]);
      await queryClient.invalidateQueries({ queryKey: examKeys.all });
      void message.success(t("exams.marksSaved"));
    },
    onError: (err) => {
      const errors = err instanceof ApiError ? err.fieldErrors() : {};
      const byCell: Record<string, string> = {};
      for (const [path, messages] of Object.entries(errors)) {
        const index = /^marks\[(\d+)\]/.exec(path)?.[1];
        const cell = index === undefined ? undefined : sent[Number(index)];
        if (cell) byCell[cell[0]] = messages.join(" ");
      }
      setCellErrors(byCell);
      setSummary(err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]);
    },
  });

  const onCellKey = (event: KeyboardEvent<HTMLInputElement>, row: number) => {
    const step = event.key === "ArrowDown" || event.key === "Enter" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    const next = grid.current?.querySelector<HTMLInputElement>(`input[data-row="${row + step}"]`);
    if (next) {
      event.preventDefault();
      next.focus();
      next.select();
    }
  };

  const columns = [
    {
      title: t("grading.student"),
      key: "student",
      render: (_: unknown, s: ExamStudentMark) => (
        <Space orientation="vertical" size={0}>
          <span>{studentFullName(s)}</span>
          {!s.onRoster && <Typography.Text type="secondary">{t("exams.offRoster")}</Typography.Text>}
        </Space>
      ),
    },
    {
      title: t("exams.markColumn", { max: Number(exam.maxScore) }),
      key: "mark",
      width: 140,
      render: (_: unknown, s: ExamStudentMark, row: number) => {
        const saved = s.mark;
        if (!editable || !s.onRoster) {
          return saved ? (
            <Tooltip title={t("grading.enteredBy", { name: saved.enteredByName, time: formatSchoolDateTime(saved.enteredAt) })}>
              <Tag color={saved.isEdited ? "gold" : "default"}>{`${Number(saved.value)}${saved.isEdited ? "*" : ""}`}</Tag>
            </Tooltip>
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          );
        }
        const value = s.studentId in draft ? (draft[s.studentId] ?? null) : saved ? Number(saved.value) : null;
        return (
          <Tooltip title={cellErrors[s.studentId]} open={cellErrors[s.studentId] ? undefined : false}>
            <InputNumber
              data-row={row}
              aria-label={t("exams.cellLabel", { name: studentFullName(s) })}
              // No min/max: InputNumber would silently clamp a typo to the maximum; the server rejects it and the cell shows why.
              precision={0}
              keyboard={false}
              controls={false}
              {...(cellErrors[s.studentId] ? { status: "error" as const } : {})}
              value={value}
              style={{ width: 80 }}
              onKeyDown={(e) => onCellKey(e, row)}
              onChange={(next) => setDraft((d) => ({ ...d, [s.studentId]: typeof next === "number" ? next : null }))}
            />
          </Tooltip>
        );
      },
    },
  ];

  const studentName = (id: string) => {
    const s = data?.students.find((x) => x.studentId === id);
    return s ? studentFullName(s) : "";
  };

  return (
    <Modal
      open={examClass !== undefined}
      title={examClass ? t("exams.sheetTitle", { name: exam.name, className: examClass.className }) : ""}
      onCancel={onClose}
      width={720}
      destroyOnHidden
      footer={
        <Space wrap>
          <Button onClick={onClose}>{t("common.close")}</Button>
          {editable && (
            <Button type="primary" loading={save.isPending} disabled={sent.length === 0} onClick={() => save.mutate()}>
              {t("common.save")}
            </Button>
          )}
        </Space>
      }
    >
      {data && (
        <Typography.Paragraph type="secondary">
          {t("exams.sheetLabel", { subject: data.subjectName, date: formatDate(data.date), grader: data.graderName })}
        </Typography.Paragraph>
      )}
      {sheet.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void sheet.refetch()}>{t("app.retry")}</Button>} />}
      {sheet.isPending && examClass !== undefined && <Table rowKey="studentId" size="small" columns={columns} dataSource={[]} loading pagination={false} />}
      {data && (
        <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
          <Space wrap size={8}>
            {data.isClosed && <Tag icon={<LockOutlined />} color="orange">{t("grading.closed")}</Tag>}
            {data.isUpcoming && <Tag color="blue">{t("exams.upcoming")}</Tag>}
            {editable && <Typography.Text type="secondary">{t("exams.keyboardHint")}</Typography.Text>}
            {sent.length > 0 && <Tag color="blue">{t("grading.unsaved")}</Tag>}
          </Space>
          {summary.length > 0 && <Alert type="error" showIcon title={summary.join(" ")} />}
          <div ref={grid}>
            <Table<ExamStudentMark>
              rowKey="studentId"
              size="small"
              columns={columns}
              dataSource={data.students}
              pagination={false}
              locale={{ emptyText: t("exams.emptyRoster") }}
              scroll={{ x: "max-content" }}
            />
          </div>
          <Collapse
            size="small"
            items={[
              {
                key: "edits",
                label: t("grading.editLog", { count: edits.data?.length ?? 0 }),
                children: (
                  <List
                    size="small"
                    loading={edits.isPending}
                    dataSource={edits.data ?? []}
                    locale={{ emptyText: t("grading.noEdits") }}
                    renderItem={(e) => (
                      <List.Item>
                        <Space orientation="vertical" size={0}>
                          <span>
                            {e.newValue === null
                              ? t("exams.editRemoved", { name: studentName(e.studentId), from: Number(e.oldValue) })
                              : t("exams.editChanged", { name: studentName(e.studentId), from: Number(e.oldValue), to: Number(e.newValue) })}
                          </span>
                          <Typography.Text type="secondary">{`${e.actorName}, ${formatSchoolDateTime(e.occurredAt)}`}</Typography.Text>
                        </Space>
                      </List.Item>
                    )}
                  />
                ),
              },
            ]}
          />
        </Space>
      )}
    </Modal>
  );
}
